<template>
  <component
    :is="to ? nuxtLink : 'div'"
    :to="to"
    :no-prefetch="true"
    :class="[
      $style['card'],
      { [$style[`card--${aspectRatio}`]]: aspectRatio },
      { [$style[`card--selectable`]]: selectable },
      { [$style[`card--clickable`]]: to || $attrs.onClick },
      { [$style[`card--active`]]: active },
      { [$style[`card--horizontal`]]: horizontal },
      { [$style[`card--skeleton`]]: skeleton },
      { [$style[`card--bordered`]]: border }
    ]">
    <div
      v-if="!!$slots['image']"
      :class="$style['card__image']">
      <slot name="image" />

      <div
        v-if="!!$slots['image-top']"
        :class="$style['card__image__top']">
        <slot name="image-top" />
      </div>

      <div
        v-if="!!$slots['image-overlay']"
        :class="$style['card__image__overlay']">
        <slot name="image-overlay" />
      </div>

      <div
        v-if="!!$slots['image-bottom']"
        :class="$style['card__image__bottom']">
        <slot name="image-bottom" />
      </div>
    </div>

    <div
      v-if="!!$slots['default'] || !!$slots['title']"
      :class="$style['card__content']">
      <div
        v-if="!!$slots['title']"
        :class="$style['card__title']">
        <slot name="title" />
      </div>
      <slot />
    </div>

    <div
      v-if="!!$slots['actions']"
      :class="$style['card__actions']">
      <slot name="actions" />
    </div>
  </component>
</template>
<script setup>

const nuxtLink = resolveComponent('nuxt-link')
defineProps({
  aspectRatio: {
    type: String,
    default: null
  },
  to: {
    type: Object,
    default: null
  },
  skeleton: {
    type: Boolean,
    default: false
  },
  border: {
    type: Boolean,
    default: false
  },
  selectable: {
    type: Boolean,
    default: false
  },
  active: {
    type: Boolean,
    default: false
  },
  horizontal: {
    type: Boolean,
    default: false
  }
})
</script>
<style lang="scss" module>
.card {
  background-color: $white;
  border-radius: $border-radius;
  color: $black;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  height: auto;
  width: 100%;

  @media screen and (prefers-color-scheme: darker) {
    background-color: $black;
    color: $grey;
  }

  &:focus-visible {
    box-shadow: $focus-visible;
  }

  .spacer {
    flex: 1;
  }

  &__title {
    // font-weight: $font-weight-bold;
  }

  &__content {
    padding: $spacing * 2;
    width: 100%;
  }

  &__image {
    background-color: $grey-lightest;
    border-radius: $border-radius $border-radius 0 0;
    overflow: hidden;
    display: inline-flex;
    position: relative;

    * {

      -webkit-user-drag: none;

    }

    @media screen and (prefers-color-scheme: darker) {
      opacity: 0.75;
      transition: opacity 1s ease;

      &:hover {
        opacity: 1;
      }
    }

    &__overlay {
      position: absolute;
      top: 75%;
      left: 0;
      right: 0;
      width: 100%;
      z-index: 3;
    }

    &__bottom {
      align-items: center;
      display: flex;
      padding: $spacing * 2;
      position: absolute;
      bottom: 0;
      right: 0;
      width: 100%;
    }

    &__top {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      padding: $spacing * 2;
      line-height: 1;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
    }
  }

  &__actions {
    align-items: center;
    justify-content: stretch;
    display: grid;
    grid-gap: $spacing;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    padding: $spacing * 2;
    width: 100%;
  }

  // Card link
  &--clickable {
    cursor: pointer;
    text-decoration: none;

    &:hover {
      background-color: $grey-lightest;
    }
  }

  &--selectable {
    cursor: pointer;

    &:hover {
      border-color: $black;
    }
  }

  &--active:is(.card--bordered) {
      border-color: $black;
  }

  &--bordered {
    border: 1px solid $grey-light;
  }

  &--square {
    .card__image {
      position: relative;
      padding-bottom: 100%;

      &>* {
        position: absolute;
        inset: 0;
        width: auto;
        height: auto;
      }
    }
  }

  &--horizontal {
    flex-direction: row;

    .card__image {
      display: flex;
      width: 33.333%;

      * {
        object-fit: cover;
        aspect-ratio: 3/2;
        min-height: 100%;
      }
    }
  }
}
</style>
